import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type {
  CreateParticipantRequest,
  UpdateParticipantRequest,
} from "src/core/backend/clients/backendClient";
import { ToastContext } from "src/core/context/ToastContext";
import { useErrorHandler } from "src/core/error/useErrorHandler";
import { useError } from "src/core/hooks/useError";
import { logService } from "src/core/services/logService";
import { textService } from "src/core/services/textService";
import { useProfile } from "src/features/auth/hooks/useProfile";
import { useSettingsStore } from "src/features/settings/services/useSettingsStore";
import {
  AddressField,
  type AddressFormData,
} from "src/features/shared/components/AddressField";
import {
  BirthDateField,
  type BirthDateFormData,
} from "src/features/shared/components/BirthDateField";
import {
  GenderField,
  type GenderFormData,
} from "src/features/shared/components/GenderField";
import {
  NameField,
  type NameFormData,
  type NameRef,
} from "src/features/shared/components/NameField";
import {
  PhoneField,
  type PhoneFormData,
} from "src/features/shared/components/PhoneField";
import {
  SSNField,
  type SSNFormData,
} from "src/features/shared/components/SSNField";
import type { ParticipantModel } from "../../models/participantModel";
import { useParticipantStore } from "../../services/useParticipantStore";
import "./ParticipantEdit.css";
import { viewService } from "src/core/services/viewService";

export type ParticipantEditProps = {
  visible: boolean;
  onHide: () => void;

  participant?: ParticipantModel;
};

export interface ParticipantEditFormData
  extends NameFormData,
    BirthDateFormData,
    GenderFormData,
    SSNFormData,
    AddressFormData,
    PhoneFormData {}

export function ParticipantEdit({
  visible,
  onHide,
  participant,
}: ParticipantEditProps) {
  const toastRef = useContext(ToastContext);
  const nameRef = useRef<NameRef>(null);

  const { t } = useTranslation();
  const { birthDateLabel, ssnLabel, addressLabel, phoneLabel } = useProfile();
  const { getErrorMessage } = useError();

  const settingsStore = useSettingsStore();
  const participantStore = useParticipantStore();
  const errorHandler = useErrorHandler();

  const form = useForm<ParticipantEditFormData>({
    mode: "all",
  });

  const participantLoad = () => {
    form.reset({
      ...participant,
      phone: participant?.phone ?? textService.formatPhone("+36"),
    } as unknown as ParticipantEditFormData);
  };

  useEffect(() => {
    if (!visible) {
      form.clearErrors();
      form.reset();
      return;
    }

    participantLoad();

    setTimeout(() => {
      if (!viewService.isMobileSize) nameRef.current?.focus();
      if (participant?.birthDate !== undefined) participantLoad();
      form.clearErrors();
    }, 100);
  }, [visible]);

  const onSave = async () => {
    if (!form.formState.isValid) return;

    const formValues = form.getValues();

    logService.log("ParticipantEdit.onSave", formValues);

    const model = { ...formValues } as ParticipantModel;

    if (model.id === 0) {
      await participantStore
        .participantPost({
          ...model,
        } as CreateParticipantRequest)
        .then(() => {
          toastRef?.current?.show({
            severity: "success",
            summary: t("Home.Participants.Title"),
            detail: t("Home.Participants.AddSuccess"),
            closable: true,
            life: 5000,
          });
        })
        .catch((error) => {
          errorHandler.setError({
            title: t("Home.Participants.Title"),
            message:
              t("Home.Participants.AddFailed") + " " + getErrorMessage(error),
          });
        });
    } else {
      await participantStore
        .participantPut({
          ...model,
        } as UpdateParticipantRequest)
        .then(() => {
          toastRef?.current?.show({
            severity: "success",
            summary: t("Home.Participants.Title"),
            detail: t("Home.Participants.UpdateSuccess"),
            closable: true,
            life: 5000,
          });
        })
        .catch((error) => {
          errorHandler.setError({
            title: t("Home.Participants.Title"),
            message:
              t("Home.Participants.UpdateFailed") +
              " " +
              getErrorMessage(error),
          });
        });
    }

    await participantStore.participantList();

    onHide();
  };

  return (
    <div className="participantEdit">
      <Dialog
        header={
          (participant?.id ?? 0) > 0
            ? t("Home.Participants.UpdateTitle")
            : t("Home.Participants.AddTitle")
        }
        modal={true}
        breakpoints={{
          "1200px": "50vw",
          "992px": "75vw",
          "768px": "90vw",
          "576px": "95vw",
        }}
        style={{
          maxHeight: "70vh",
          minWidth: "300px",
          maxWidth: "90vw",
          width: "30vw",
        }}
        draggable={false}
        resizable={false}
        maximizable={false}
        closeOnEscape={false}
        visible={visible}
        onHide={onHide}
        footer={
          <div className="flex justify-content-end">
            <Button
              type="button"
              label={t("Home.Profile.Save")}
              icon="pi pi-check"
              severity="success"
              disabled={!form.formState.isValid}
              loading={participantStore.loading}
              onClick={onSave}
            ></Button>

            <Button
              type="button"
              label={t("Base.Cancel")}
              icon="pi pi-times"
              severity="warning"
              onClick={onHide}
            ></Button>
          </div>
        }
      >
        <div className="field grid">
          <label
            htmlFor="name"
            className="col-12 md:col-4 font-bold"
          >
            {t("Home.Registration.Name")} *:
          </label>
          <div className="col-12 md:col-8">
            <NameField
              ref={nameRef}
              form={form}
              required={true}
            />
          </div>
        </div>

        <div className="field grid">
          <label
            htmlFor="birthDate"
            className="col-12 md:col-4 font-bold"
          >
            {birthDateLabel}:
          </label>
          <div className="col-12 md:col-8">
            <BirthDateField
              form={form}
              required={settingsStore.legacySettings.birthDateRequired ?? false}
            />
          </div>
        </div>

        <div className="field grid">
          <label className="col-12 md:col-4 font-bold">
            {t("Home.Registration.Gender")} *:
          </label>
          <div className="col-12 md:col-8 flex gap-2">
            <GenderField
              form={form}
              required={true}
            />
          </div>
        </div>

        <div className="field grid">
          <label
            htmlFor="ssn"
            className="col-12 md:col-4 font-bold"
          >
            {ssnLabel}:
          </label>
          <div className="col-12 md:col-8">
            <SSNField
              form={form}
              required={settingsStore.legacySettings.ssnRequired ?? false}
            />
          </div>
        </div>

        <div className="field grid">
          <label className="col-12 md:col-4 font-bold">{addressLabel}:</label>
          <div className="col-12 md:col-8">
            <AddressField
              form={form}
              required={settingsStore.legacySettings.addressRequired ?? false}
            />
          </div>
        </div>

        <div className="field grid">
          <label
            htmlFor="phone"
            className="col-12 md:col-4 font-bold"
          >
            {phoneLabel}:
          </label>
          <div className="col-12 md:col-8">
            <PhoneField
              form={form}
              required={settingsStore.legacySettings.phoneRequired ?? false}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
